<script>
import Vue from "vue";
import {mapActions, mapGetters, mapState} from "vuex";
import VueMasonry from "vue-masonry-css";
import CandidateCard from "@/components/election/CandidateCard.vue";
import RichText from "@/components/ui/RichText.vue";
import Breadcrumbs from "@/components/ui/Breadcrumbs.vue";

Vue.use(VueMasonry);

export default {
  name: "Elections",
  components: {RichText, CandidateCard, Breadcrumbs},
  data: () => ({
    selectedCommune: null,
    selectedElectionType: null,
    selectedPosition: 'Alle Ämter',
    selectedElectionResult: null,
    availableElectionResults: [
      {text: 'Wahlstatus', value: null},
      {text: 'Gewählt', value: true },
      {text: 'Nicht gewählt', value: false },
    ]
  }),
  computed: {
    ...mapState('elections', ['candidates']),
    ...mapGetters('elections', ['communeFilterValues', 'electionTypeFilterValues', "positionFilterValues", "globalNote", "getLocalNote", "electionCompleted", "electionInfoUrl", "electionName"]),
    candidatesForPosition() {
      return this.candidates
          .filter(candidate => this.selectedPosition === 'Alle Ämter' || candidate.position === this.selectedPosition);
    },
    enableElectedSelect() {
      return this.candidatesForPosition?.some(candidate => candidate.elected) ?? false;
    },
    filteredCandidates() {
      return this.candidatesForPosition
          .filter(candidate => this.selectedElectionResult === null || (this.enableElectedSelect && candidate.elected === this.selectedElectionResult));
    },
    localNote() {
      return this.getLocalNote(this.selectedCommune);
    },
    showNotes() {
      return (this.localNote && this.selectedCommune) || this.globalNote || this.electionInfoUrl
    },
    masonryConfig() {
      if(this.showNotes) {
        return { default: 2, 600: 1 }
      }
      return {
        default: 3,
        800: 2,
        600: 1
      }
    }
  },
  watch: {
    electionTypeFilterValues(newVal) {
      if (newVal.length > 0 && !this.selectedElectionType) {
        this.selectedElectionType = newVal[0].value;
      }

      this.onFilterChanged();
    }
  },
  mounted() {
    if(this.electionTypeFilterValues.length > 0) {
      this.selectedElectionType = this.electionTypeFilterValues[0].value;
      this.onFilterChanged();
    }
  },
  methods: {
    ...mapActions('elections', ['loadCandidates']),
    onFilterChanged() {
      const filter = {
        // communeId: this.selectedCommune,
        electionTypeId: this.selectedElectionType,
      }

      if(isNaN(this.selectedCommune)) {
        filter.externalCommune = this.selectedCommune;
      } else {
        filter.communeId = this.selectedCommune;
      }

      this.loadCandidates(filter);
    }
  }
}
</script>

<template>
    <v-row class="mt-7">
      <v-col cols="12">
        <breadcrumbs />
        <div class="custom-header-text">Politische Akteure</div>
      </v-col>
      <v-col cols="12" sm="3">
        <v-select v-model="selectedCommune" @change="onFilterChanged" :items="communeFilterValues" single-line hide-details outlined dense>
        </v-select>
      </v-col>
      <v-col cols="12" sm="3">
        <v-select v-model="selectedElectionType" @change="onFilterChanged" :items="electionTypeFilterValues" single-line hide-details outlined dense />
      </v-col>
      <v-col cols="12" sm="3">
        <v-select v-model="selectedPosition" :items="positionFilterValues" single-line hide-details outlined dense />
      </v-col>
      <v-col cols="12" sm="3">
        <v-select v-model="selectedElectionResult" :items="availableElectionResults" :disabled="!enableElectedSelect" single-line hide-details outlined dense />
      </v-col>
      <v-col cols="12" sm="4" v-if="showNotes">
        <div class="note" v-if="globalNote">
          <rich-text :source="globalNote" />
        </div>
        <div class="note" v-if="localNote && selectedCommune">
          <rich-text :source="localNote" />
        </div>
        <div class="info-box" v-if="electionInfoUrl">
          <span class="election-badge">{{ electionName }}</span>
          <div class="primary-text info-heading">Der Landeswahlleiter informiert zur {{ electionName }}</div>
          <p>
            Über folgenden Link finden Sie alle wichtigen Infos zu Fristen, Wahltermin und Briefwahl.
          </p>
          <a :href="electionInfoUrl" target="_blank">
            <v-icon color="primary" small left>mdi-open-in-new</v-icon>
            Zur Infoseite
          </a>
        </div>
      </v-col>
      <v-col cols="12" :sm="showNotes ? 8 : 12">
        <masonry :cols="masonryConfig" gutter="16px">
          <candidate-card v-for="candidate in filteredCandidates" :key="candidate.id" :candidate="candidate" />
        </masonry>
      </v-col>
    </v-row>
</template>

<style scoped>
.note {
  background: #DBBBD6 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;
  padding: 24px 18px;
  margin-bottom: 16px;
}

.info-box {
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;
  padding: 24px 18px;
  background-color: white;
}

.election-badge {
  font-size: 12px;
  font-family: "GeometriaMedium", sans-serif;
  background-color: #DDDDDD;
  border-radius: 2px;
  padding: 2px 6px;
}

.info-heading {
  margin-top: 8px;
  font-family: "GeometriaMedium", sans-serif;
}
</style>
